// React Query
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
// Http Methods
import { getRequest as get, putRequest as put } from '../utils/HttpMethods';
import queryString from '../utils/queryStringBuilder';

// Interfaces
import { isUser, User } from 'interfaces';
import { isSettingsState, useSettingsStore } from 'store/SettingsStore';

import { SettingsState } from 'store/SettingsStore';

import { useMutationQuery } from 'api/utils/queries/useMutationQuery';
import {
  createEntity,
  updateEntity,
  useGetQuery,
  useListQuery,
} from '../utils/queries';

import * as Sentry from '@sentry/react';

export const userQueryKeys = {
  all: ['users'],
  list: () => [...userQueryKeys.all, 'list'],
  listByOrganization: (organizationID: string) => [
    ...userQueryKeys.all,
    organizationID,
  ],
  listBySite: (siteID: string) => [...userQueryKeys.all, 'site', siteID],
  get: (email: string) => [...userQueryKeys.all, email],
  getUserByCognitoID: (cognitoID: string) => [...userQueryKeys.all, cognitoID],
  getUserByProviderID: (providerID: string) => [
    ...userQueryKeys.all,
    providerID,
  ],
  settings: () => [...userQueryKeys.all, 'settings'],
};

// Fetch user settings (current authenticated user)
export const getUserSettings = async (
  userID: string
): Promise<SettingsState | null> =>
  await get<SettingsState>(
    'QBilling',
    `/resource/user/settings${queryString({ userID })}`
  );

// Update user settings (complete object expected by backend)
export const updateUserSettings = async (s: {
  userID: string;
  body: Partial<SettingsState>;
}): Promise<SettingsState | null> =>
  await put<SettingsState>('QBilling', '/resource/user/settings', s);

// Hooks
export const useGetUserSettings = ({
  enabled = true,
  displayNotification = false,
}: {
  enabled?: boolean;
  displayNotification?: boolean;
} = {}): UseQueryResult<SettingsState, Error> => {
  return useGetQuery<SettingsState>({
    queryKey: userQueryKeys.settings(),
    fetchFunction: getUserSettings,
    functionName: 'getUserSettings',
    isEntity: isSettingsState,
    params: undefined,
    enabled,
    displayNotification,
  });
};

export const useUpdateUserSettings = (): UseMutationResult<
  SettingsState,
  Error
> => {
  return useMutationQuery<SettingsState>({
    mutationFn: updateUserSettings,
    successMessage: 'Settings Saved',
    errorMessage: 'Error Saving Settings',
    queryKey: userQueryKeys.settings(),
    displayNotification: false,
  });
};

export const hydrateSettingsFromServer = async (
  userID: string
): Promise<void> => {
  try {
    const remote = await getUserSettings(userID);
    if (
      remote &&
      isSettingsState(remote) &&
      !useSettingsStore.getState().dirty
    ) {
      useSettingsStore.getState().loadSettings(remote);
      useSettingsStore.getState().markSynced();
    }
  } catch (error) {
    Sentry.captureException(error);
  }
};

export const pushDirtySettings = async (
  userID: string,
  state: Partial<SettingsState>
): Promise<boolean> => {
  const payload: { userID: string; body: Partial<SettingsState> } = {
    userID,
    body: {
      ...state,
    },
  };
  try {
    const updated = await updateUserSettings(payload);
    if (updated) {
      return true;
    }
  } catch (e) {
    Sentry.captureException(e);
  }
  return false;
};

/**
 * @description Fetches a user by email.
 * @param {string} email - The email of the user.
 * @returns {Promise<User | null>} The user data or null if not found.
 */
export const getUser = async (email: string): Promise<User | null> => {
  return await get<User>('QBilling', `/resource/user${queryString({ email })}`);
};

/**
 * @description Fetches a user by Cognito ID.
 * @param {string} cognitoID - The Cognito ID of the user.
 * @returns {Promise<User | null>} The user data or null if not found.
 */
const getUserByCognitoID = async (cognitoID: string): Promise<User | null> => {
  return await get<User>(
    'QBilling',
    `/resource/user/cognitoID${queryString({ cognitoID })}`
  );
};

const getUserByProviderID = async (
  providerID: string
): Promise<User | null> => {
  return await get<User>(
    'QBilling',
    `/resource/user/providerID${queryString({ providerID })}`
  );
};

/**
 * @description Lists users by organization ID.
 * @param {string} orgID - The organization ID.
 * @returns {Promise<User[]>} An array of users.
 */
const listUsersByOrganization = async (
  organizationID: string
): Promise<User[]> =>
  await get<User[]>(
    'QBilling',
    `/resource/user/organization${queryString({ organizationID })}`
  );

/**
 * @description Lists users by site ID.
 * @param {string} siteID - The site ID.
 * @returns {Promise<User[]>} An array of users.
 */
const listUsersBySite = async (siteID: string): Promise<User[]> =>
  await get<User[]>(
    'QBilling',
    `/resource/user/site${queryString({ siteID })}`
  );

/**
 * @description Creates a new user.
 * @param {User} user - The user data.
 * @returns {Promise<User>} The created user.
 */
const createUser = async (user: User): Promise<User> => {
  return createEntity<User>({
    path: '/resource/user',
    entity: user,
    functionName: 'createUser',
    isEntity: isUser,
  });
};

/**
 * @description Updates an existing user.
 * @param {User} user - The user data.
 * @returns {Promise<User>} The updated user.
 */
const updateUser = async (user: User): Promise<User> => {
  return updateEntity<User>({
    entity: user,
    functionName: 'updateUser',
    isEntity: isUser,
    path: '/resource/user',
  });
};

/**
 * @description Disables a user by ID.
 * @param {string} id - The user ID.
 * @returns {Promise<User>} The disabled user.
 */
export const disableUser = async (id: string): Promise<User> => {
  return updateEntity<User>({
    entity: { id } as User,
    functionName: 'disableUser',
    isEntity: isUser,
    path: '/resource/user/disable',
  });
};

export type MFAUpdate =
  | {
      method: 'sms';
      enabled: true;
      phone: string;
      email: string;
    }
  | {
      method: 'totp';
      enabled: true;
      email: string;
    }
  | {
      method: 'none';
      enabled: false;
      email: string;
    };

const updateMFAPreference = async (update: MFAUpdate) => {
  return updateEntity<User>({
    entity: update as unknown as User,
    functionName: 'updateMFAPreference',
    isEntity: isUser,
    path: '/resource/user/mfa',
  });
};

/**
 * @description Hook to list users by organization ID.
 * @param {Object} params - The parameters.
 * @param {string} params.orgID - The organization ID.
 * @param {boolean} [params.displayNotification=false] - Whether to display a notification.
 * @returns {UseQueryResult<User[], Error>} The query result containing an array of users.
 */
export const useListUsersByOrganization = ({
  orgID,
  displayNotification = false,
}: {
  orgID: string;
  displayNotification?: boolean;
}): UseQueryResult<User[], Error> => {
  return useListQuery<User>({
    queryKey: userQueryKeys.listByOrganization(orgID),
    fetchFunction: listUsersByOrganization,
    functionName: 'listUsersByOrganization',
    isEntity: isUser,
    params: orgID,
    enabled: !!orgID,
    displayNotification,
  });
};

/**
 * @description Hook to list users by site ID.
 * @param {Object} params - The parameters.
 * @param {string} params.siteID - The site ID.
 * @param {boolean} [params.displayNotification=false] - Whether to display a notification.
 * @returns {UseQueryResult<User[], Error>} The query result containing an array of users.
 */
export const useListUsersBySite = ({
  siteID,
  displayNotification = false,
}: {
  siteID: string;
  displayNotification?: boolean;
}): UseQueryResult<User[], Error> => {
  return useListQuery<User>({
    queryKey: userQueryKeys.listBySite(siteID),
    fetchFunction: listUsersBySite,
    functionName: 'listUsersBySite',
    isEntity: isUser,
    params: siteID,
    enabled: !!siteID,
    displayNotification,
  });
};

/**
 * @description Hook to fetch a user by email.
 * @param {string} email - The email of the user.
 * @returns {UseQueryResult<User | [] | null>} The query result containing the user data or null if not found.
 */
export const useGetUserByEmail = (
  email: string
): UseQueryResult<User | [] | null> => {
  return useGetQuery<User>({
    queryKey: userQueryKeys.get(email),
    fetchFunction: getUser,
    functionName: 'getUser',
    params: email,
    isEntity: isUser,
    enabled: !!email,
  });
};

/**
 * @description Hook to fetch a user by Cognito ID.
 * @param {string} cognitoID - The Cognito ID of the user.
 * @returns {UseQueryResult<User | null>} The query result containing the user data or null if not found.
 */
export const useGetUserByCognitoID = (
  cognitoID: string
): UseQueryResult<User | null> => {
  return useGetQuery<User>({
    queryKey: userQueryKeys.getUserByCognitoID(cognitoID),
    fetchFunction: getUserByCognitoID,
    functionName: 'getUserByCognitoID',
    params: cognitoID,
    isEntity: isUser,
    enabled: !!cognitoID,
  });
};

/**
 * @description Hook to fetch a user by provider ID.
 * @param {string} providerID - The provider ID.
 * @returns {UseQueryResult<User | null>} The query result containing the user data or null if not found.
 */
export const useGetUserByProviderID = (
  providerID: string
): UseQueryResult<User | null> => {
  return useGetQuery<User>({
    queryKey: userQueryKeys.getUserByProviderID(providerID),
    fetchFunction: getUserByProviderID,
    functionName: 'getUserByProviderID',
    params: providerID,
    isEntity: isUser,
    enabled: !!providerID,
  });
};

/**
 * @description Hook to create a new user.
 * @returns {UseMutationResult<User, Error>} The mutation result for creating a user.
 */
export const useCreateUser = (): UseMutationResult<User, Error> => {
  return useMutationQuery({
    mutationFn: createUser,
    successMessage: 'User Created',
    errorMessage: 'Error Creating User',
    queryKey: userQueryKeys.all,
  });
};

/**
 * @description Hook to update an existing user.
 * @returns {UseMutationResult<User, Error>} The mutation result for updating a user.
 */
export const useUpdateUser = (): UseMutationResult<User, Error> => {
  return useMutationQuery({
    mutationFn: updateUser,
    successMessage: 'User Updated',
    errorMessage: 'Error Updating User',
    queryKey: userQueryKeys.all,
  });
};

/**
 * @description Hook to disable a user by ID.
 * @returns {UseMutationResult<User, Error>} The mutation result for disabling a user.
 */
export const useDisableUser = (): UseMutationResult<User, Error> => {
  return useMutationQuery({
    mutationFn: disableUser,
    successMessage: 'User Disabled',
    errorMessage: 'Error Disabling User',
    queryKey: userQueryKeys.all,
  });
};

export const useUpdateMFA = (): UseMutationResult<User, Error> => {
  return useMutationQuery({
    mutationFn: updateMFAPreference,
    successMessage: 'MFA Preference Changed',
    errorMessage: 'Error Changing MFA Preference',
    queryKey: userQueryKeys.all,
    displayNotification: false,
  });
};
